import dayjs from "dayjs";
import type { Opportunity } from "@api/opportunity";
import type { ColumnDef } from "./columnConfig";

export const formatDate = (dateStr: string | null): string => {
  if (!dateStr) return "—";
  const d = dayjs(dateStr);
  return d.isValid() ? d.format("MMM D, YYYY") : "—";
};

/** Display the primary date column as a range when a (different) end date exists, else a single date. */
export const formatDateRange = (start: string | null, end: string | null): string => {
  if (!start) return "—";
  if (end && !dayjs(end).isSame(dayjs(start), "day")) {
    return `${formatDate(start)} – ${formatDate(end)}`;
  }
  return formatDate(start);
};

/** The read-only display value for a cell (range for the primary date column, count for future stops). */
export const getDisplayValue = (opp: Opportunity, col: ColumnDef): string => {
  if (col.isPrimarySort) return formatDateRange(opp.primary_date, opp.end_date);
  if (col.isInt) return opp.is_derived ? String(opp.future_stops) : "—";
  return opp.fields[col.field] ?? "";
};

/**
 * Derived (Pollstar-managed) cells are read-only: the date range, future-stops count, and the artist/
 * tour name come from Pollstar and are overwritten on re-derivation. Future-stops is always read-only.
 * Other columns (LD, PM, Christie reps, …) stay editable even on derived rows — those are preserved
 * across re-derivation.
 */
export const isCellReadOnly = (opp: Opportunity, col: ColumnDef, isAdmin: boolean): boolean => {
  if (col.isInt) return true;
  if (opp.is_derived && (col.isPrimarySort || col.field === "artist_tour")) return true;
  // Non-admins may only edit columns an admin flagged "Open Edit" (checked after the system-read-only
  // rules above so a derived date / future-stops can never be edited even if flagged).
  if (!isAdmin && !col.openEdit) return true;
  return false;
};
